import { isPreviewVideo } from "../previewMedia";
import { isSareeLikeProduct } from "../productSareeFields";

/**
 * Product photos as "views" (owner decision 2026-10-27). Each photo may carry an optional `view`; empty
 * view tiles are the reminder that blouse, back or video are still missing. Nothing here is required to
 * save or publish. Mirrors the backend `ProductMediaViews`.
 */
import { normalizeView, PHOTO_VIEWS, PhotoView } from "./photoViewNames";

export { normalizeView, PHOTO_VIEWS };
export type { PhotoView };
/** Coverage slot for a video of any view; never a photo's `view`. */
export const VIDEO_SLOT = "VIDEO";
export type ViewSlot = PhotoView | typeof VIDEO_SLOT;

export const VIEW_LABEL: Record<ViewSlot, string> = {
  FULL: "Full",
  PALLU: "Pallu",
  BORDER: "Border",
  BLOUSE: "Blouse",
  BACK: "Back",
  DETAIL: "Detail",
  VIDEO: "Video",
};

/** What the empty tile's sheet says the view is for. */
export const VIEW_HINT: Record<ViewSlot, string> = {
  FULL: "The whole piece, draped or laid flat",
  PALLU: "The end piece, where the design is richest",
  BORDER: "Along the length, close enough to see the weave",
  BLOUSE: "Buyers look for the blouse fabric and its border",
  BACK: "The reverse side shows the weave is real",
  DETAIL: "A close-up of a motif or the weave",
  VIDEO: "10–30 seconds of the drape or the weave",
};

export const SAREE_SLOTS: ViewSlot[] = ["FULL", "PALLU", "BORDER", "BLOUSE", "BACK", "VIDEO"];
export const OTHER_SLOTS: ViewSlot[] = ["FULL", "BACK", "DETAIL", "VIDEO"];

const asText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const row = value as { label?: unknown; name?: unknown; value?: unknown };
    if (typeof row.label === "string") return row.label;
    if (typeof row.name === "string") return row.name;
    if (typeof row.value === "string") return row.value;
  }
  return "";
};

/** True once any classification level is filled; before that the Add flow does not know the piece yet. */
const hasClassification = (form: any): boolean =>
  ["artform", "type", "category", "subCategory"].some((key) => asText(form?.[key]).trim().length > 0);

/**
 * Which views this piece asks for. Sarees get six; anything else four. A piece without a category yet
 * (step 1 of Add) is treated as a saree, the common case in HKP studios.
 */
export const viewSlotsFor = (form: any): ViewSlot[] =>
  !hasClassification(form) || isSareeLikeProduct(form) ? SAREE_SLOTS : OTHER_SLOTS;

export const normalizeSkipped = (raw: unknown): ViewSlot[] => {
  if (!Array.isArray(raw)) return [];
  const out: ViewSlot[] = [];
  raw.forEach((value) => {
    const view = String(value || "").trim().toUpperCase();
    if ((view === VIDEO_SLOT || normalizeView(view)) && !out.includes(view as ViewSlot)) out.push(view as ViewSlot);
  });
  return out;
};

export const isShownRow = (row: any): boolean => !(row?.show === false || row?.show === 0 || row?.show === "0");
export const isPinnedRow = (row: any): boolean => row?.pin === true || row?.pin === 1 || row?.pin === "1";
export const isVideoRow = (row: any): boolean => isPreviewVideo(row);
/** The slot a row fills: VIDEO for any video, else its view, else null (untagged). */
export const slotOf = (row: any): ViewSlot | null => (isVideoRow(row) ? VIDEO_SLOT : normalizeView(row?.view));

export type CoverageSlot = { view: ViewSlot; label: string; count: number; skipped: boolean };
export type Coverage = {
  slots: CoverageSlot[];
  missing: ViewSlot[];
  done: number;
  total: number;
  untagged: number;
};

/** Hidden rows and rows without a file do not count: buyers never see them. */
export const coverageOf = (photos: any[], slots: ViewSlot[], skipped: ViewSlot[] = []): Coverage => {
  const counts = new Map<ViewSlot, number>();
  let untagged = 0;
  (photos || []).forEach((row) => {
    if (!row || row._deleted || !isShownRow(row)) return;
    const slot = slotOf(row);
    if (!slot) {
      untagged += 1;
      return;
    }
    counts.set(slot, (counts.get(slot) || 0) + 1);
  });
  const rows: CoverageSlot[] = slots.map((view) => ({
    view,
    label: VIEW_LABEL[view],
    count: counts.get(view) || 0,
    skipped: skipped.includes(view),
  }));
  const live = rows.filter((r) => !r.skipped);
  return {
    slots: rows,
    missing: live.filter((r) => r.count === 0).map((r) => r.view),
    done: live.filter((r) => r.count > 0).length,
    total: live.length,
    untagged,
  };
};

/** "3 of 6 · missing blouse, back, video" */
export const coverageSummary = (cov: Coverage): string => {
  const base = `${cov.done} of ${cov.total}`;
  if (!cov.missing.length) return `${base} · all views added`;
  return `${base} · missing ${cov.missing.map((v) => VIEW_LABEL[v].toLowerCase()).join(", ")}`;
};

/** Plain words for the review and publish sheets: "blouse", "back", "video". */
export const missingWords = (missing: ViewSlot[]): string[] => missing.map((v) => VIEW_LABEL[v].toLowerCase());

/** The cover is the pinned photo, else the first shown photo (not a video); same rule the list uses. */
export const coverRowIndex = (photos: any[]): number => {
  const rows = photos || [];
  const pinned = rows.findIndex((r) => r && !r._deleted && isShownRow(r) && isPinnedRow(r) && !isVideoRow(r));
  if (pinned >= 0) return pinned;
  return rows.findIndex((r) => r && !r._deleted && isShownRow(r) && !isVideoRow(r));
};

const RANK: Record<string, number> = { FULL: 0, PALLU: 1, BORDER: 2, BLOUSE: 3, BACK: 4, DETAIL: 5 };

/**
 * Buyer gallery order: cover first, then full, pallu, border, blouse, back, detail, untagged, then
 * videos. Stable, so photos within a view keep the studio's order. The cover never moves, so tagging
 * never changes what a product's card shows.
 */
export const orderMediaByView = <T = any>(rows: T[] | null | undefined): T[] => {
  const list = Array.isArray(rows) ? rows : [];
  const cover = coverRowIndex(list as any[]);
  const rank = (row: any) => (isVideoRow(row) ? 7 : RANK[normalizeView(row?.view) || ""] ?? 6);
  return list
    .map((row, index) => ({ row, index, rank: index === cover ? -1 : rank(row) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((x) => x.row);
};

/** Which photo each tile shows, and what is left for the "More photos" strip. */
export const splitForTiles = (
  photos: any[],
  slots: ViewSlot[]
): { tiles: Map<ViewSlot, number>; rest: number[] } => {
  const rows = photos || [];
  const cover = coverRowIndex(rows);
  const tiles = new Map<ViewSlot, number>();
  rows.forEach((row, index) => {
    if (!row || row._deleted) return;
    const slot = slotOf(row);
    if (!slot || !slots.includes(slot) || !isShownRow(row)) return;
    const current = tiles.get(slot);
    // The cover wins its own tile; otherwise the first shown photo of that view.
    if (current == null || (index === cover && current !== cover)) tiles.set(slot, index);
  });
  const used = new Set(tiles.values());
  const rest = rows.map((_, i) => i).filter((i) => rows[i] && !rows[i]._deleted && !used.has(i));
  return { tiles, rest };
};
